import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowDown01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { IconButton, Menu, MenuItem, TextField } from "@outpave/ds";
import { useTranslation } from "react-i18next";

export interface RateOrAmountOption {
  /** Stored value, e.g. InvoiceDiscountType.PERCENTAGE. */
  value: string;
  /** The unit's mark, e.g. "%", "$", or a card icon, on the trigger and its menu row. */
  glyph: React.ReactNode;
  /** The unit's name in the menu, e.g. "Percentage". */
  name: string;
  /** A line under the name, where the unit needs explaining. */
  description?: string;
}

interface RateOrAmountFieldProps {
  id: string;
  /**
   * The Formik path VALIDATION reports against, which on these three rows is
   * not the path the field writes: the input holds `discount` while the rule
   * runs on the derived `discountAmount`. Required, because `handleBlur`
   * reads `event.target.name` and falls back to `id`, so without it blur
   * marks `invoice-discount` touched, the validated key stays untouched, and
   * `hasAnyError` hides the message until Save touches every field.
   */
  name: string;
  /** Names the field and its unit menu for screen readers. */
  ariaLabel: string;
  options: RateOrAmountOption[];
  mode: string;
  amount: string | number;
  error?: boolean;
  /**
   * The figure is computed rather than entered, so the input goes inert and
   * reads Auto. The unit menu stays live, or the row could never switch back.
   */
  disabled?: boolean;
  onModeChange: (mode: string) => void;
  onAmountChange: (amount: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const FIELD_SX = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    { WebkitAppearance: "none", margin: 0 },
};

// Sized to the unit mark and its chevron rather than to the theme's square.
const TRIGGER_SX = {
  width: "auto",
  paddingInline: "var(--space-2)",
  gap: "var(--space-1)",
  fontSize: "var(--type-size-md)",
};

const DESCRIPTION_SX = {
  display: "block",
  color: "var(--color-text-tertiary)",
};

/**
 * A figure and the unit it is expressed in, for the three summary rows that
 * take either a percentage or a flat amount. The unit is a menu inside the
 * field rather than a row of toggles, so three units still leave room to type.
 */
const RateOrAmountField: React.FC<RateOrAmountFieldProps> = ({
  id,
  name,
  ariaLabel,
  options,
  mode,
  amount,
  error,
  disabled,
  onModeChange,
  onAmountChange,
  onBlur,
}) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const active = options.find((option) => option.value === mode) ?? options[0];
  const menuId = `${id}-unit-menu`;

  const pick = (value: string) => () => {
    setMenuAnchor(null);
    if (value !== mode) onModeChange(value);
  };

  return (
    <>
      <TextField
        id={id}
        name={name}
        type="number"
        size="md"
        fullWidth
        value={disabled || amount === 0 ? "" : amount}
        placeholder={disabled ? t("Auto") : "0"}
        error={error}
        onChange={(event) => onAmountChange(event.target.value)}
        onBlur={onBlur}
        // The row's visible label is not associated with the input, so a
        // reader landing on the figure otherwise gets an unnamed field.
        inputProps={{ "aria-label": ariaLabel, min: 0, disabled }}
        sx={FIELD_SX}
        trailing={
          <IconButton
            variant="ghost"
            size="small"
            aria-label={t("rateOrAmountUnitLabel", {
              field: ariaLabel,
              unit: active?.name,
            })}
            aria-haspopup="menu"
            aria-expanded={!!menuAnchor}
            aria-controls={menuAnchor ? menuId : undefined}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={TRIGGER_SX}
          >
            {active?.glyph}
            <HugeiconsIcon icon={ArrowDown01Icon} size={14} />
          </IconButton>
        }
      />
      <Menu
        id={menuId}
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selection="checkmark"
            selected={option.value === mode}
            trailing={option.glyph}
            onClick={pick(option.value)}
          >
            {option.name}
            {option.description && (
              <Typography
                variant="caption"
                component="span"
                sx={DESCRIPTION_SX}
              >
                {option.description}
              </Typography>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default RateOrAmountField;
